import { AREA_UTIL, FOLHA, LAYOUTS, estiloDaImagem, type Tamanho } from "@foto/shared";
import { cx } from "../../components/ui.js";
import { CamadasNaCelula } from "../camadas/CamadasNaCelula.js";
import type { FigurinhasDasCamadas } from "../camadas/figurinhas-das-camadas.js";
import {
  ehEspacoDeMarcador,
  recorteDaFaixa,
  tamanhoMostrado,
  telaDaCelula,
  type Folha,
  type FotoNaBandeja,
  type Tamanhos,
} from "./folhas.js";

/** Um marcador pronto como a prévia precisa: a imagem da folha do conjunto, de onde a faixa sai recortada. */
export interface MarcadorPronto {
  titulo: string;
  tela: string;
}

const emPorcento = (mm: number, total: number) => `${(mm / total) * 100}%`;

/**
 * A folha como vai sair, desenhada em escala na tela.
 *
 * Cada foto é posicionada pela mesma conta que o servidor usa (`estiloDaImagem`,
 * em `@foto/shared`): a célula recorta, a foto é uma imagem absoluta por trás
 * dela. A conferência final é o "Ver como vai sair", que mostra o JPEG de
 * verdade; esta prévia serve para montar sem esperar o servidor a cada toque.
 *
 * As figurinhas, os logos e os textos de cada foto vão por cima dela, pela mesma conta
 * do servidor (`@foto/shared`): posição, giro e tamanho são os verdadeiros, mesmo quando
 * a miniatura é pequena.
 */
export function PreviewDaFolha({
  folha,
  fotos,
  tamanhos,
  selecionada,
  aoEscolher,
  figurinhas,
  marcadoresProntos,
}: {
  folha: Folha;
  fotos: Map<number, FotoNaBandeja>;
  /** Os marcadores já gerados, pelo id: a faixa de cada espaço de marcador sai recortada da folha deles (#115). */
  marcadoresProntos?: Map<number, MarcadorPronto> | undefined;
  tamanhos: Tamanhos;
  /** O que a biblioteca diz das figurinhas que as camadas citam. Sem isto, as imagens ficam como "carregando". */
  figurinhas?: FigurinhasDasCamadas | undefined;
  selecionada: number | null;
  aoEscolher: (indice: number) => void;
}) {
  const layout = LAYOUTS[folha.layout];

  return (
    <div
      className="@container relative w-full overflow-hidden rounded-peca bg-white shadow-lg"
      style={{ aspectRatio: `${FOLHA.larguraMm} / ${FOLHA.alturaMm}` }}
    >
      {layout.celulas.map((c, i) => {
        const posicao = {
          left: emPorcento(c.x, FOLHA.larguraMm),
          top: emPorcento(c.y, FOLHA.alturaMm),
          width: emPorcento(c.largura, FOLHA.larguraMm),
          height: emPorcento(c.altura, FOLHA.alturaMm),
        };
        if (ehEspacoDeMarcador(folha.layout, i)) {
          const m = folha.marcadores?.[i];
          const pronto = m ? marcadoresProntos?.get(m.marcadorId) : undefined;
          const ativa = selecionada === i;
          return (
            <button
              key={i}
              type="button"
              onClick={() => aoEscolher(i)}
              aria-label={
                m
                  ? `Espaço ${i + 1}: marcador${pronto ? ` “${pronto.titulo}”` : ""}, faixa ${m.faixa + 1}`
                  : `Espaço ${i + 1}, marcador vazio`
              }
              aria-pressed={ativa}
              className={cx(
                "absolute overflow-hidden",
                m ? "bg-neutral-200" : "border border-dashed border-neutral-400 bg-neutral-50",
                ativa && "z-10 outline outline-[3px] -outline-offset-[3px] outline-brasa",
              )}
              style={posicao}
            >
              {m && pronto ? (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 bg-no-repeat"
                  style={{ backgroundImage: `url(${pronto.tela})`, ...recorteDaFaixa(m.faixa) }}
                />
              ) : !m ? (
                <span className="absolute inset-0 flex items-center justify-center px-1 text-center text-[0.6875rem] leading-tight text-neutral-500">
                  Marcador
                </span>
              ) : null}
            </button>
          );
        }
        const dentro = folha.celulas[i];
        const foto = dentro ? fotos.get(dentro.fotoId) : undefined;
        // A imagem da célula: a ampliação de cenário, a foto da IA ou a peça publicada.
        const tamanho: Tamanho | undefined = dentro ? tamanhoMostrado(dentro, tamanhos) : undefined;
        const origem = dentro ? telaDaCelula(dentro, foto) : undefined;
        const estilo =
          dentro && tamanho
            ? estiloDaImagem(dentro.enquadramento, tamanho, {
                largura: c.largura,
                altura: c.altura,
              })
            : null;
        const ativa = selecionada === i;

        return (
          <button
            key={i}
            type="button"
            onClick={() => aoEscolher(i)}
            aria-label={
              dentro ? `Foto ${i + 1}: ${foto?.nome ?? "foto"}` : `Espaço ${i + 1}, vazio`
            }
            aria-pressed={ativa}
            className={cx(
              "absolute overflow-hidden",
              dentro ? "bg-neutral-100" : "border border-dashed border-neutral-400 bg-neutral-50",
              ativa && "z-10 outline outline-[3px] -outline-offset-[3px] outline-brasa",
            )}
            style={{
              left: emPorcento(c.x, FOLHA.larguraMm),
              top: emPorcento(c.y, FOLHA.alturaMm),
              width: emPorcento(c.largura, FOLHA.larguraMm),
              height: emPorcento(c.altura, FOLHA.alturaMm),
            }}
          >
            {origem && estilo ? (
              <img
                src={origem}
                alt=""
                draggable={false}
                className="pointer-events-none absolute max-w-none select-none"
                style={{
                  left: `${estilo.left}%`,
                  top: `${estilo.top}%`,
                  width: `${estilo.width}%`,
                  height: `${estilo.height}%`,
                }}
              />
            ) : !dentro ? (
              <span className="absolute inset-0 flex items-center justify-center text-[0.75rem] text-neutral-500">
                {i + 1}
              </span>
            ) : null}
            {dentro?.camadas && dentro.camadas.length > 0 && (
              <CamadasNaCelula
                camadas={dentro.camadas}
                figurinhas={figurinhas}
                celula={{ largura: c.largura, altura: c.altura }}
              />
            )}
            {dentro?.ampliada && (
              <span
                aria-label="Cenário ampliado por IA"
                className="pointer-events-none absolute bottom-0.5 right-0.5 rounded-sm bg-black/65 px-1 text-[0.625rem] leading-4 text-white"
              >
                IA
              </span>
            )}
          </button>
        );
      })}

      {/* O nome da pessoa embaixo de cada foto, na faixa que vai fora no corte (#113), como sai no papel. A letra
          acompanha a largura da prévia: 2,1% da folha é o corpo que o servidor usa na faixa de 3 mm. */}
      {layout.legendas?.map((f, i) => {
        const dentro = folha.celulas[i];
        const nome = dentro ? fotos.get(dentro.fotoId)?.nome?.trim() : undefined;
        if (!nome) return null;
        return (
          <span
            key={`nome-${i}`}
            aria-hidden
            className="pointer-events-none absolute flex items-center justify-center overflow-hidden px-[0.8cqw] leading-none text-neutral-700"
            style={{
              left: emPorcento(f.x, FOLHA.larguraMm),
              top: emPorcento(f.y, FOLHA.alturaMm),
              width: emPorcento(f.largura, FOLHA.larguraMm),
              height: emPorcento(f.altura, FOLHA.alturaMm),
              fontSize: "2.1cqw",
            }}
          >
            <span className="truncate">{nome}</span>
          </span>
        );
      })}

      {/* A área útil do papel: o que passa dela cai no picotado e se perde. Só na tela. */}
      <span
        aria-hidden
        data-area-util
        className="pointer-events-none absolute z-20 border border-dashed border-sky-500/80"
        style={{
          left: emPorcento((FOLHA.larguraMm - AREA_UTIL.larguraMm) / 2, FOLHA.larguraMm),
          top: emPorcento((FOLHA.alturaMm - AREA_UTIL.alturaMm) / 2, FOLHA.alturaMm),
          width: emPorcento(AREA_UTIL.larguraMm, FOLHA.larguraMm),
          height: emPorcento(AREA_UTIL.alturaMm, FOLHA.alturaMm),
        }}
      />

      {/* As guias de corte, como saem no papel. */}
      {layout.guias.map((g, i) => (
        <span
          key={i}
          aria-hidden
          className="pointer-events-none absolute bg-neutral-500/70"
          style={{
            left: emPorcento(Math.min(g.x1, g.x2), FOLHA.larguraMm),
            top: emPorcento(Math.min(g.y1, g.y2), FOLHA.alturaMm),
            width: g.x1 === g.x2 ? "1px" : emPorcento(Math.abs(g.x2 - g.x1), FOLHA.larguraMm),
            height: g.y1 === g.y2 ? "1px" : emPorcento(Math.abs(g.y2 - g.y1), FOLHA.alturaMm),
          }}
        />
      ))}
    </div>
  );
}

/** O desenho pequeno de um formato, para escolher entre eles. */
export function MiniaturaDoFormato({ layout }: { layout: Folha["layout"] }) {
  return (
    <span
      aria-hidden
      className="relative block h-12 shrink-0 overflow-hidden rounded-[2px] bg-white"
      style={{ aspectRatio: `${FOLHA.larguraMm} / ${FOLHA.alturaMm}` }}
    >
      {LAYOUTS[layout].celulas.map((c, i) => (
        <span
          key={i}
          // O espaço de marcador num tom mais escuro: na escolha do formato, já se vê onde vai o marcador.
          className={cx(
            "absolute",
            LAYOUTS[layout].tipos?.[i] === "marcador" ? "bg-neutral-600" : "bg-neutral-400",
          )}
          style={{
            left: emPorcento(c.x, FOLHA.larguraMm),
            top: emPorcento(c.y, FOLHA.alturaMm),
            width: emPorcento(c.largura, FOLHA.larguraMm),
            height: emPorcento(c.altura, FOLHA.alturaMm),
          }}
        />
      ))}
    </span>
  );
}
